import React, { useEffect } from 'react'
import Navigation from '../../../Components/Navigation/Navigation'
import Footer from '../../../Components/Footer/Footer'
import Hero from './Sections/Hero/Hero'
import IndustryChallengesAndSolutions from './Sections/IndustryChallengesAndSolutions/IndustryChallengesAndSolutions'
import IndustryTech from './Sections/IndustryTech/IndustryTech'
import AISearch from './Sections/AISearch/AISearch'
import SpecializedServices from './Sections/SpecializedServices/SpecializedServices'
import WhyChoose from './Sections/WhyChoose/WhyChoose'
import FAQ from '../../../Components/FAQ/FAQ'
import Aos from 'aos'
import 'aos/dist/aos.css'
import f1 from '../../../assets/images/f1.png'
import f2 from '../../../assets/images/f2.png'
import f9 from '../../../assets/images/f9.png'
import ClientSpeak from '../../Home/Components/ClientSpeak'
import GetQuote from '../../../Components/GetQuote/GetQuote'
import Contect from '../../Home/Components/Contect'
import CaseStudies from '../../Home/Components/CaseStudies'

const Fitness = () => {

	useEffect(() => {
		Aos.init({
			duration: 1000,
			once: true,
			offset: 100,
		});
	}, []);

	const homeContent = {
		title: 'Fitness App Development',
		description: "Monotonous exercise regimens, 6AM push notifications, and no customization aren't motivation—they're digital harassment. Users want apps that adapt to busy lives, celebrate small wins, and feel like a supportive friend—not a trainer they can't quit.",
		image: f1,
		breadcrumb: 'Fitness',
		b: "Let's Talk Solutions"
	}

	const industryChallengesAndSolutionsContent = {
		challengesTitle: 'The Deadly Sins That Kill Fitness App Success',
		solutionsTitle: 'The Science-Backed Solutions That Create Fitness Addicts',
		challenges: [
			{
				title: 'Your User Data Privacy Is Weaker Than Their Willpower',
				description: "Users know you're collecting everything. If they can't trust you with personal data, they'll delete faster than a GDPR notice.",
				icon: '/images/industries/fitness/01.svg',
			},
			{
				title: "Cookie-Cutter Workouts That Don't Match Real Lives",
				description: "Generic 30-day challenges ignore reality. Busy parents need 15-minute routines—not hour-long gym sessions with equipment they don't own.",
				icon: '/images/industries/fitness/02.svg',
			},
			{
				title: 'Users Hit Plateaus and Abandon Your App Forever',
				description: 'When progress stalls, the app should adapt. Most keep pushing the same routines as motivation crashes and uninstall rates spike.',
				icon: '/images/industries/fitness/03.svg',
			},
			{
				title: 'Motivation Features That Actually Demotivate Users',
				description: 'Aggressive streaks, shame-y nudges, and influencer comparisons create a digital bully—users feel worse and churn.',
				icon: '/images/industries/fitness/04.svg',
			},
			{
				title: "Your App Can't Connect With Wearables That Actually Matter",
				description: "If Apple Watch or Fitbit won't sync flawlessly, your app feels broken and outdated.",
				icon: '/images/industries/fitness/05.svg',
			},
		],
		solutions: [
			{
				title: 'Bank-Level Security That Defends Personal Health Data',
				description: 'HIPAA compliance, end-to-end encryption, and zero-knowledge architecture. Protect heart rate data like online banking.',
				icon: '/images/industries/fitness/06.svg',
			},
			{
				title: 'AI-Powered Workouts That Adapt to Real Life',
				description: 'ML analyzes sleep, stress, schedules, and preferences. Busy Tuesday? 10-minute routine. Energetic Saturday? Turn it up.',
				icon: '/images/industries/fitness/07.svg',
			},
			{
				title: 'Progress Tracking That Breaks Through Plateaus',
				description: 'Algorithms detect stagnation early. Recalibrate goals, swap exercise variants, and auto-adjust programs to keep momentum.',
				icon: '/images/industries/fitness/08.svg',
			},
			{
				title: 'Behavior Psychology-Based Motivation Systems',
				description: 'Positive reinforcement, habit stacking, and micro-rewards create addiction to progress—not guilt.',
				icon: '/images/industries/fitness/09.svg',
			},
			{
				title: 'Seamless Integration With Every Device Users Own',
				description: 'Apple Health, Google Fit, Fitbit, Garmin, smart scales, HR monitors—one dashboard that truly syncs everything.',
				icon: '/images/industries/fitness/10.svg',
			},
		]
	}

	const industryTechContent = {
		mainTitle: 'The Revolutionary Tech Stack Transforming Fitness',
		leftHeading: 'Harnessing Advanced Technologies For Fitness App Development',
		leftParagraph: 'We combine adaptive AI coaching, computer vision, robust wearable integrations, and gamified systems to transform motivation into daily habit.',
		leftImage: f2,
		performanceItems: [
			{
				title: 'AI Personal Trainers & Adaptive Workout Generation',
				description: 'Machine learning creates custom routines that adjust to progress, preferences, and real-time performance data.',
				icon: '/images/industries/fitness/12.svg',
			},
			{
				title: 'Real-Time Form Correction by Computer Vision',
				description: 'Advanced motion analysis reduces injury risk and maximizes effectiveness with instant feedback on every rep.',
				icon: '/images/industries/fitness/13.svg',
			},
			{
				title: 'Wearable Device Integration & Health Monitoring',
				description: 'Seamless connections to smartwatches, trackers, and sensors for a real-time picture of health.',
						icon: '/images/industries/fitness/14.svg',
			},
			{
				title: 'Gamification & Social Competition Features',
				description: 'Achievement systems, leaderboards, and team challenges build supportive communities and healthy competition.',
				icon: '/images/industries/fitness/15.svg',
			},
		]
	};

	const aiSearchContent = {
		mainTitle: 'The Complete Fitness Ecosystem That Actually Works',
		sections: [
			{
				title: 'Personalized AI Fitness Coach',
				description: 'A coach that understands your users\' lives, adapts programs in real time, and celebrates progress.',
				image: '/images/industries/fitness/f3.png',
				imageAlt: 'Personalized AI Fitness Coach',
				imagePosition: 'left',
				features: [
					'Learns fitness level, preferences, and time constraints',
					'Adapts to sleep quality, stress, and recovery',
					'Real-time prompts and encouragement during workouts',
					'Calendar sync to plan around life commitments',
					'Long-term pattern tracking to avoid plateaus',
					'Equipment and injury-aware alternatives on demand',
				],
			},
			{
				title: 'Real-Time Workout Tracking & Form Analysis',
				description: 'Computer vision counts reps, analyzes movement, and prevents bad habits with instant feedback.',
				image: '/images/industries/fitness/f4.png',
				imageAlt: 'Real-Time Workout Tracking & Form Analysis',
				imagePosition: 'right',
				features: [
					'Camera-based movement and alignment analysis',
					'Instant audio feedback for corrections',
					'Automatic rep and set counting—no manual input',
					'Injury risk detection with safer exercise swaps',
					'Rich mobility and strength analytics',
					'Bodyweight, weights, yoga, and cardio support',
				],
			},
			{
				title: 'Nutrition Planning & Meal Prep Assistant',
				description: 'Meal plans that actually fit budgets, skills, family needs, and real life—not fantasy diets.',
				image: '/images/industries/fitness/f5.png',
				imageAlt: 'Nutrition Planning & Meal Prep Assistant',
				imagePosition: 'left',
				features: [
					'Weekly plans aligned to training and diet',
					'Auto grocery lists with cost and aisle estimates',
					'Step-by-step cooking and prep optimizations',
					'Photo-based macro and calorie tracking',
					'Healthy restaurant suggestions without guilt',
					'Family and batch-cooking adaptations',
				],
			},
			{
				title: 'Social Challenges & Community Features',
				description: 'Real accountability with encouraging communities, not empty leaderboards and shame loops.',
				image: '/images/industries/fitness/f6.png',
				imageAlt: 'Social Challenges & Community Features',
				imagePosition: 'right',
				features: [
					'Micro-communities by level, location, and schedule',
					'Team challenges that promote cooperation',
					'Virtual workout buddies with synced sessions',
					'Peer support groups for specific goals',
					'Private progress sharing with partners',
					'Rewards for helpfulness and engagement',
				],
			},
			{
				title: 'Progress Analytics & Goal Setting Dashboard',
				description: 'Turn biometrics and workout logs into actionable insight that improves outcomes.',
				image: '/images/industries/fitness/f7.png',
				imageAlt: 'Progress Analytics & Goal Setting Dashboard',
				imagePosition: 'left',
				features: [
					'Trends across strength, endurance, flexibility, body composition',
					'Correlate lifestyle and performance patterns',
					'Predictive timelines for goals and expectations',
					'Benchmarks vs similar demographics',
					'Weekly summaries with specific adjustments',
					'Mood and energy integrate to tune intensity',
				],
			},
			{
				title: 'Wearable Integration & Health Sync',
				description: 'One place where everything connects—wearables, health apps, and medical devices.',
				image: '/images/industries/fitness/f3.png',
				imageAlt: 'Wearable Integration & Health Sync',
				imagePosition: 'right',
				features: [
					'Apple Health, Google Fit, Fitbit, Garmin +50 more',
					'Unify HR, sleep, steps, and recovery data',
					'Auto-adjust intensity from real-time HRV',
					'Connect smart scales, BP monitors, glucose meters',
					'Provider/insurer APIs for wellness programs',
					'Offline-first syncing without data loss',
				],
			},
		],
	};

	const servicesContent = {
		title: 'The Development Services That Build Fitness Empires',
		cards: [
			{
				icon: '/images/industries/fitness/16.svg',
				title: 'Custom AI Algorithm Development',
				description: 'ML models for personalized workouts and adaptive coaching systems.',
				buttonText: 'Discover More',
			},
			{
				icon: '/images/industries/fitness/17.svg',
				title: 'Wearable Device & Health Kit Integration',
				description: 'Seamless connectivity to wearables and health platforms to unify data.',
				buttonText: 'Discover More',
			},
			{
				icon: '/images/industries/fitness/18.svg',
				title: 'Computer Vision & Motion Tracking',
				description: 'Camera-based form analysis and real-time correction to prevent injuries.',
				buttonText: 'Discover More',
			},
			{
				icon: '/images/industries/fitness/19.svg',
				title: 'Cross-Platform Mobile Development',
				description: 'High-performance native iOS and Android experiences across devices.',
				buttonText: 'Discover More',
			},
			{
						icon: '/images/industries/fitness/20.svg',
				title: 'Gamification & Social Feature Development',
				description: 'Achievement systems, community challenges, and social connectivity features.',
				buttonText: 'Discover More',
			},
			{
				icon: '/images/industries/fitness/16.svg',
				title: 'Advanced Analytics & Progress Tracking',
				description: 'Visual dashboards and predictive analytics that turn data into action.',
				buttonText: 'Discover More',
			},
		],
	};

	const whyChooseContent = {
		title: 'Why Leading Fitness Brands Choose Our Development Team',
		image: f9,
		imageAlt: 'Why Choose Us for Fitness App Development',
		items: [
			{
				title: 'Deep Fitness Industry & Exercise Science Knowledge',
				desc: 'We understand biomechanics, periodization, and recovery science—not just code.',
			},
			{
				title: 'Apps Built on Proven Behavioral Psychology',
				desc: 'Habit formation, motivation loops, and nudges grounded in peer-reviewed research.',
			},
			{
				title: 'Health Data Security That Passes All Standards',
				desc: 'HIPAA, SOC 2, and military-grade encryption protect sensitive health data.',
			},
			{
				title: 'Scalable Platforms for Growing Fitness Communities',
				desc: 'Cloud architecture handles viral surges without crashes or slowdowns.',
			},
			{
				title: 'Developers Who Actually Use Fitness Apps',
				desc: 'Trainers and enthusiasts on our team build from lived user experience.',
			},
		],
	};

	const faqContent = {
		title: 'What Every Founder Must Answer Before Launch',
		questions: [
			{
				q: 'What do you do to keep users motivated after the first month?',
				a: 'Behavioral psychology, micro-rewards, adaptive goals, and community features that form real habits.'
			},
			{
				q: 'Which wearable devices and health platforms integrate?',
				a: 'Apple Health, Google Fit, Fitbit, Garmin, Samsung Health, and 50+ more via APIs.'
			},
			{
				q: 'How do you individualize workouts across fitness levels?',
				a: 'AI analyzes preferences and performance to automatically generate adaptive plans.'
			},
			{
				q: 'How do you handle sensitive health and fitness data?',
				a: 'E2E encryption, HIPAA compliance, zero-knowledge design, and rigorous data governance.'
			}
		]
	};

	return (
		<>
			<Navigation offer={true} />
			<Hero homeContent={homeContent} />
			<div data-aos="fade-up">
				<IndustryChallengesAndSolutions industryChallengesAndSolutionsContent={industryChallengesAndSolutionsContent} />
			</div>
			<div data-aos="fade-up">
				<IndustryTech industryTechContent={industryTechContent} />
			</div>
			<div data-aos="fade-up">
				<AISearch aiSearchContent={aiSearchContent} />
			</div>
			<div data-aos="fade-up">
				<SpecializedServices servicesContent={servicesContent} />
			</div>
			<div data-aos="fade-up">
				<WhyChoose whyChooseContent={whyChooseContent} />
			</div>
			<div data-aos="fade-up">
				<CaseStudies />
			</div>
			<div data-aos="fade-up">
				<ClientSpeak />
			</div>
			<div data-aos="fade-up">
				<GetQuote />
			</div>
			<div data-aos="fade-up">
				<FAQ faqContent={faqContent} />
			</div>
			<div data-aos="fade-up">
				<Contect />
			</div>
			<Footer />
		</>
	)
}

export default Fitness


